import { useEffect, useRef, type ReactNode } from 'react';

interface TerminalPanelProps {
  /** Section mnemonic, e.g. MKT, FX, CTG. */
  mnemonic: string;
  title: string;
  /** Right-hand status. Defaults to the schematic notice. */
  status?: string;
  /** Source chips along the footer. */
  sources?: string[];
  children: ReactNode;
  className?: string;
}

/**
 * Panel chrome shared by every schematic on the site.
 *
 * Reproduces the product's real panel anatomy — index, title, one dot per bound
 * source, and a state on the right — so the marketing pages show the actual
 * interface rather than an illustration of one.
 *
 * The status reads "SCHEMATIC · NO DATA" by default and the dots are hollow.
 * That is not decoration: these panels are not connected to anything, and the
 * product's rule is that an unbound panel says so rather than inventing a
 * plausible number.
 */
export function TerminalPanel({
  mnemonic,
  title,
  status = 'SCHEMATIC · NO DATA',
  sources = [],
  children,
  className,
}: TerminalPanelProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      el.setAttribute('data-panel', 'in');
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute('data-panel', 'in');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`tpanel glass ${className ?? ''}`} data-panel="">
      <header className="tpanel__bar">
        <span className="tpanel__mn">{mnemonic}</span>
        <span className="tpanel__title">{title}</span>
        <span className="tpanel__dots" aria-hidden="true">
          {(sources.length ? sources : ['unbound']).slice(0, 4).map((s) => (
            <i key={s} title={`${s} — not reachable from this page`} />
          ))}
        </span>
        <span className="tpanel__status">{status}</span>
      </header>

      <div className="tpanel__body">{children}</div>

      {sources.length ? (
        <footer className="tpanel__foot">
          {sources.map((s) => (
            <span key={s}>{s}</span>
          ))}
        </footer>
      ) : null}
    </div>
  );
}
